import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { NotificationBell } from '@/components/notifications';
import { ActiveStatesIndicator } from './ActiveStatesIndicator';
import { exitGodModeSafely } from '@/services/agency.service';
import { toast } from '@/utils/toast';
import { useTranslation } from 'react-i18next';
import { agencyRoleLabelKey } from '@/utils/agencyRole';
import { useRoleLabel } from '@/hooks/useRoleLabel';
import { devLog } from '@/utils/logger';
import { useCrispChat } from '@/components/CrispChat';
import { CalmMode } from '@/components/dashboard/CalmMode';
import { cn } from '@/utils/cn';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { Icon } from '@/components/ui/Icon';

/**
 * Dashboard Navbar Component
 *
 * Top navigation bar for authenticated pages
 * - Fixed position at top
 * - Height: 4.1rem (65.6px)
 * - White background with subtle shadow
 * - Dynamic logo (workspace → organization → default)
 * - God Mode indicator (when agency user impersonates client)
 * - Filters indicator (when search/filters are active)
 * - Role-based user menu
 */

export const DashboardNavbar = () => {
  const { user, logout, refreshUser } = useAuth();
  const { branding } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExitingGodMode, setIsExitingGodMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common');
  const { t: tAgency } = useTranslation('agency');
  const { t: tSettings } = useTranslation('settings');
  const roleLabel = useRoleLabel();
  const { openChat } = useCrispChat();
  const { title: pageTitle, icon: pageIcon, isMinimized: showPageTitle } = usePageTitle();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      devLog.error('Logout failed:', error);
    }
  };

  /**
   * Exit God Mode
   * Calls API to end impersonation session, returns user to agency view
   */
  const handleExitGodMode = async () => {
    setIsExitingGodMode(true);

    try {
      await exitGodModeSafely();
    } catch (error) {
      // Genuine failure (network/5xx). Do not trap the user: still clear
      // local state and land on the portfolio below.
      devLog.error('[DashboardNavbar] Failed to exit God Mode:', error);
      toast.error(t('error_exit_god_mode'));
    }
    try {
      // Refresh user data to clear godModeSession
      await refreshUser();
    } catch (error) {
      devLog.error('[DashboardNavbar] refreshUser failed after exit:', error);
    }
    setIsDropdownOpen(false);
    setIsExitingGodMode(false);
    window.location.href = '/agency';
  };

  if (!user) {
    return null;
  }

  // Get user avatar from preferences
  const userAvatar = (user.preferences as { avatar?: string } | null)?.avatar || null;

  // Atelier Mode (whitelabel branding) is a PORTFOLIO-level feature. Hide it only
  // when the user is actually acting as a client: agency staff in God Mode, or a
  // real client user (not agency staff) whose team belongs to an agency. Agency
  // owners/staff viewing their own portfolio (not God Mode) should still see it,
  // since it's how they reach agency whitelabel settings.
  const hideAtelierMode: boolean =
    !!user?.godModeSession || (!user?.isAgencyStaff && user?.team?.agencyId != null);

  return (
    <nav
      className="fixed top-0 left-0 right-0 h-navbar-height bg-white shadow-navbar z-navbar"
      aria-label={t('aria_top_navigation')}
    >
      <div className="h-full px-2 sm:px-4 flex items-center justify-between gap-0 sm:gap-2 lg:gap-4">
        {/* Left Side: Logo + God Mode Indicator */}
        <div className="flex min-w-0 flex-1 items-center gap-0 sm:flex-initial sm:gap-4">
          {/* Dynamic Logo (workspace → whitelabel → default) */}
          <Link to="/" className="flex items-center justify-center lg:w-[56px] max-sm:min-w-[44px] max-sm:min-h-[44px] flex-shrink-0" aria-label={t('aria_dashboard_home')}>
            <img
              src={user?.currentTeam?.logo || branding.logoSmall || '/images/aa-logo-simple.png'}
              alt={
                user?.currentTeam?.logo
                  ? (user.currentTeam.name || 'Ägile Ässets')
                  : (branding.workspaceName || branding.organizationName || 'Ägile Ässets')
              }
              className="h-8 max-w-[44px] sm:max-w-[66px] lg:max-w-[56px] object-contain transition-opacity duration-200"
              loading="eager"
              decoding="async"
              onError={(e) => {
                const img = e.currentTarget;
                const teamLogo = user?.currentTeam?.logo;
                const whitelabelLogo = branding.logoSmall;
                const defaultLogo = '/images/aa-logo-simple.png';

                if (teamLogo && img.src.endsWith(teamLogo) && whitelabelLogo) {
                  // Team logo failed, try whitelabel
                  img.src = whitelabelLogo;
                } else {
                  // Whitelabel logo failed (or no whitelabel), use default
                  img.src = defaultLogo;
                }
              }}
            />
          </Link>

          {/* Combined: God Mode + Impersonation + Filters
              Desktop: renders each individually (unchanged)
              Mobile 0-1 active: renders each individually (own pills)
              Mobile 2+ active: single combined pill + unified dropdown */}
          <div className="flex min-w-0 flex-1 justify-center sm:contents">
            <ActiveStatesIndicator />
          </div>
        </div>

        {/* Center: Page Title (shown when PageHeader is scrolled past) — absolutely centered on the viewport */}
        <div
          className={cn(
            'fixed top-0 h-navbar-height left-0 right-0 items-center justify-center gap-2 pointer-events-none transition-opacity duration-300',
            'hidden lg:flex',
            showPageTitle && pageTitle ? 'opacity-100' : 'opacity-0'
          )}
        >
          {pageIcon && <Icon name={pageIcon} size="sm" color="text-gray-400" ariaHidden />}
          <span className="font-wagner font-semibold text-gray-700" style={{ fontSize: 'clamp(15px, 2vh, 19px)' }}>
            {pageTitle}
          </span>
        </div>

        {/* Right Side: Notifications + User Dropdown */}
        <div className="flex shrink-0 items-center gap-0 sm:gap-3">
          {/* Calm Mode */}
          <CalmMode />

          {/* Live Chat */}
          <button
            onClick={openChat}
            className="relative inline-flex items-center justify-center max-sm:min-w-[44px] max-sm:min-h-[44px] p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
            aria-label={t('open_chat')}
          >
            <svg
              style={{ width: 'clamp(18px, 2.5vh, 24px)', height: 'clamp(18px, 2.5vh, 24px)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </button>

          {/* User Notification Bell */}
          <NotificationBell />

          {/* User Dropdown */}
          <div className="relative shrink-0" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center justify-center sm:justify-start gap-0 sm:gap-2 max-sm:h-[44px] max-sm:w-[44px] hover:bg-gray-50 rounded-full sm:pr-2 sm:ml-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            aria-label={t('aria_user_menu')}
          >
            {/* User Avatar */}
            {userAvatar ? (
              <img
                src={userAvatar}
                alt={user?.fullName || 'User'}
                className="rounded-full object-cover"
                style={{ width: 'clamp(32px, 4vh, 40px)', height: 'clamp(32px, 4vh, 40px)' }}
                onError={(e) => {
                  // Hide broken image and show fallback
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`rounded-full bg-primary-500 text-primary-contrast flex items-center justify-center font-semibold ${userAvatar ? 'hidden' : ''}`} style={{ width: 'clamp(32px, 4vh, 40px)', height: 'clamp(32px, 4vh, 40px)' }}>
              {user?.fullName ? user.fullName.charAt(0).toUpperCase() : '?'}
            </div>

            {/* Dropdown Arrow */}
            <svg
              className={`hidden sm:block text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
              style={{ width: 'clamp(12px, 1.8vh, 16px)', height: 'clamp(12px, 1.8vh, 16px)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 border border-gray-100"
              role="menu"
            >
              {/* User Info Section */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{user?.fullName || 'User'}</p>
                <p className="text-xs text-gray-500 mt-1">{user?.email || ''}</p>
                {(user?.agencyRole ?? user?.teamRole ?? user?.role) && (
                  <p className="text-xs text-gray-400 mt-1 capitalize">
                    {user.agencyRole ? tAgency(agencyRoleLabelKey(user.agencyRole)) : roleLabel(user.teamRole ?? user.role)}
                  </p>
                )}
              </div>

              {/* Role-Based Menu Items */}

              {/* Agency Users: Exit God Mode (only when in God Mode) */}
              {user?.isAgencyStaff && user?.godModeSession && (
                <button
                  onClick={handleExitGodMode}
                  disabled={isExitingGodMode}
                  className="w-full flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  role="menuitem"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {isExitingGodMode ? t('action_exiting') : t('action_exit_god_mode')}
                </button>
              )}

              {/* All Users: Profile */}
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('menu_my_profile')}
              </Link>

              {/* All Users: Settings (contextual based on role) */}
              <Link
                to={user?.isAgencyStaff && !user?.godModeSession ? '/agency/settings' : '/settings'}
                className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {user?.isAgencyStaff && !user?.godModeSession ? t('menu_agency_settings') : t('menu_account_settings')}
              </Link>

              <Link
                to="/shop"
                state={{ entry: 'nav' }}
                className="flex min-h-[44px] items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                role="menuitem"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 9h16l-1 11H5L4 9Z" />
                  <path d="M9 9V7a3 3 0 0 1 6 0v2" />
                </svg>
                {tSettings('shop_title')}
              </Link>

              {/* All Users (excluding agency-client brand spaces): Atelier Mode (whitelabel/brand customization) */}
              {!hideAtelierMode && (
                <Link
                  to={user?.isAgencyStaff && !user?.godModeSession ? '/agency/settings/whitelabel' : '/settings/whitelabel'}
                  className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  role="menuitem"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  {t('menu_atelier_mode', 'Atelier Mode')}
                </Link>
              )}

              {/* All Users: Help */}
              <Link
                to="/help"
                className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('menu_help_support')}
              </Link>

              {/* All Users: Status & Feedback */}
              <Link
                to="/status"
                className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('menu_status_feedback')}
              </Link>

              {/* Super Admin Only: Admin Panel Links */}
              {user?.isSuperAdmin && (
                <>
                  <hr className="my-2 border-gray-100" />
                  <a
                    href="/admin"
                    className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-primary-700 hover:bg-primary-50 transition-colors"
                    role="menuitem"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    {t('menu_backend_admin')}
                  </a>
                  <Link
                    to="/design-system"
                    className="flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-primary-700 hover:bg-primary-50 transition-colors"
                    role="menuitem"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
                    </svg>
                    {t('menu_design_system', 'Design System')}
                  </Link>
                </>
              )}

              <hr className="my-2 border-gray-100" />

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 max-sm:min-h-[44px] text-sm text-red-600 hover:bg-red-50 transition-colors"
                role="menuitem"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('menu_logout')}
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </nav>
  );
};
